// OWMap BSL1 — Onboarding overlay (first-run "魔力 15 秒")
// 4-step cinematic intro: invitation → firefly → stillness → enter
// Visual philosophy: each step is a "breath" — space, poetry, restraint.

const { useState: useStateOnb, useEffect: useEffectOnb, useRef: useRefOnb } = React;

function Onboarding({ onDone, onOpenTutorial }) {
  const [step, setStep] = useStateOnb(0);
  const [exiting, setExiting] = useStateOnb(false);
  const [prevStep, setPrevStep] = useStateOnb(-1);
  const TOTAL = 4;

  const finish = () => {
    setExiting(true);
    setTimeout(onDone, 750);
  };

  const next = () => {
    if (step < TOTAL - 1) { setPrevStep(step); setStep(step + 1); }
    else finish();
  };

  const goTo = (i) => { if (i !== step) { setPrevStep(step); setStep(i); } };

  return (
    <div className={`onb-overlay ${exiting ? 'onb-exit' : ''}`}>
      {/* Ambient layers */}
      <div className="onb-nebula onb-nebula-1" />
      <div className="onb-nebula onb-nebula-2" />
      <div className="onb-star-field" aria-hidden="true">
        {/* 30 scattered CSS stars */}
        {Array.from({ length: 30 }, (_, i) => (
          <span key={i} className="onb-star" style={{
            left: `${(i * 37 + 7) % 100}%`,
            top:  `${(i * 53 + 13) % 100}%`,
            animationDelay: `${(i * 0.4) % 4}s`,
            width:  `${[1,1,1,2][i%4]}px`,
            height: `${[1,1,1,2][i%4]}px`,
            opacity: 0.2 + (i % 5) * 0.1,
          }} />
        ))}
      </div>

      <button className="onb-skip" onClick={finish}>跳过</button>

      {/* Step indicator — top center */}
      <div className="onb-step-indicator">
        <span className="onb-step-cur">{String(step + 1).padStart(2, '0')}</span>
        <span className="onb-step-sep">/</span>
        <span className="onb-step-tot">{String(TOTAL).padStart(2, '0')}</span>
      </div>

      {/* Main content */}
      <div className="onb-content" key={step}>
        {step === 0 && <OnbStep0 />}
        {step === 1 && <OnbStep1 />}
        {step === 2 && <OnbStep2 />}
        {step === 3 && <OnbStep3 onOpenTutorial={() => { finish(); setTimeout(onOpenTutorial, 300); }} />}
      </div>

      {/* Footer */}
      <div className="onb-foot">
        {/* Dot nav */}
        <div className="onb-dots" role="tablist" aria-label="步骤导航">
          {Array.from({ length: TOTAL }, (_, i) => (
            <button
              key={i}
              className={`onb-dot ${i === step ? 'onb-dot-on' : ''} ${i < step ? 'onb-dot-past' : ''}`}
              onClick={() => goTo(i)}
              role="tab"
              aria-selected={i === step}
              aria-label={`步骤 ${i + 1}`}
            />
          ))}
        </div>

        <button className="onb-next" onClick={next}>
          <span>{step === TOTAL - 1 ? '进入夜空' : '继续'}</span>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none">
            <path d="M5 12H19M14 7l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {step < TOTAL - 1 && (
          <button className="onb-jump-end" onClick={finish}>
            直接进入
          </button>
        )}
      </div>
    </div>
  );
}

// ── Step 0: The Invitation ────────────────────────────────────────────────────
function OnbStep0() {
  return (
    <div className="onb-step onb-step-anim">
      <div className="onb-globe-ring" aria-hidden="true">
        <svg viewBox="0 0 320 320" width="200" height="200" className="onb-globe-svg">
          <defs>
            <radialGradient id="globeGlow" cx="50%" cy="50%">
              <stop offset="0%"   stopColor="#F5C842" stopOpacity="0.15"/>
              <stop offset="60%"  stopColor="#F5C842" stopOpacity="0.04"/>
              <stop offset="100%" stopColor="#F5C842" stopOpacity="0"/>
            </radialGradient>
            <radialGradient id="globeCore" cx="40%" cy="38%">
              <stop offset="0%"   stopColor="#1a3a6e"/>
              <stop offset="100%" stopColor="#050D18"/>
            </radialGradient>
          </defs>
          {/* Glow halo */}
          <circle cx="160" cy="160" r="130" fill="url(#globeGlow)" className="onb-globe-glow-ring"/>
          {/* Globe body */}
          <circle cx="160" cy="160" r="96" fill="url(#globeCore)" stroke="rgba(245,200,66,0.2)" strokeWidth="1"/>
          {/* Equator */}
          <ellipse cx="160" cy="160" rx="96" ry="24" fill="none" stroke="rgba(245,200,66,0.18)" strokeWidth="0.8" className="onb-globe-eq"/>
          {/* Meridian */}
          <ellipse cx="160" cy="160" rx="24" ry="96" fill="none" stroke="rgba(245,200,66,0.1)" strokeWidth="0.6"/>
          {/* Story pins */}
          {[
            { cx: 128, cy: 128, r: 3.5, color: '#D44832', delay: '0s' },
            { cx: 188, cy: 148, r: 3,   color: '#5B8FD6', delay: '0.5s' },
            { cx: 152, cy: 174, r: 3.5, color: '#F5C842', delay: '1s' },
            { cx: 172, cy: 130, r: 2.5, color: '#3DAD6B', delay: '1.5s' },
            { cx: 140, cy: 162, r: 2.5, color: '#E07840', delay: '2s' },
          ].map((p, i) => (
            <g key={i}>
              <circle cx={p.cx} cy={p.cy} r={p.r * 5} fill={p.color} opacity="0" className="onb-pin-aura" style={{ animationDelay: p.delay }}/>
              <circle cx={p.cx} cy={p.cy} r={p.r} fill={p.color} className="onb-pin-dot" style={{ animationDelay: p.delay }}/>
            </g>
          ))}
        </svg>
      </div>

      <div className="onb-eyebrow">OWMAP · BSL1 · 睡前故事</div>
      <h1 className="onb-h">
        <span className="onb-h-line">每一个故事</span>
        <span className="onb-h-line onb-h-italic">都有一个</span>
        <span className="onb-h-line onb-h-gold">坐标</span>
      </h1>
      <p className="onb-p">
        在 OWMap，地球不是地图，是一架<em>留声机</em>。<br/>
        旋转它，让某个夜晚某人留下的故事，找到你。
      </p>

      <div className="onb-meta-row">
        <div className="onb-meta-item">
          <span className="onb-meta-num">5,420</span>
          <span className="onb-meta-lbl">坐标</span>
        </div>
        <div className="onb-meta-divider"/>
        <div className="onb-meta-item">
          <span className="onb-meta-num">183</span>
          <span className="onb-meta-lbl">国家与地区</span>
        </div>
        <div className="onb-meta-divider"/>
        <div className="onb-meta-item">
          <span className="onb-meta-num">6</span>
          <span className="onb-meta-lbl">文化圈</span>
        </div>
      </div>
    </div>
  );
}

// ── Step 1: The Fireflies ─────────────────────────────────────────────────────
function OnbStep1() {
  return (
    <div className="onb-step onb-step-anim">
      <div className="onb-eyebrow">第 一 幕 · 萤 火</div>
      <h1 className="onb-h">
        <span className="onb-h-line">每一盏亮光</span>
        <span className="onb-h-line onb-h-gold">都是一座灯塔</span>
      </h1>

      {/* Enhanced culture circle demo */}
      <div className="onb-cultures-demo" aria-hidden="true">
        {[
          { color: '#D44832', label: '东亚',   x: '20%',  y: '42%',  size: 52, delay: '0s',   dur: '3.1s' },
          { color: '#5B8FD6', label: '欧洲',   x: '72%',  y: '28%',  size: 44, delay: '0.6s', dur: '3.8s' },
          { color: '#D4A843', label: '中东',   x: '55%',  y: '62%',  size: 40, delay: '1.2s', dur: '3.4s' },
          { color: '#3DAD6B', label: '南亚',   x: '78%',  y: '65%',  size: 36, delay: '1.8s', dur: '4.0s' },
          { color: '#B05A32', label: '美洲',   x: '36%',  y: '72%',  size: 38, delay: '0.9s', dur: '3.6s' },
          { color: '#E07840', label: '非洲',   x: '48%',  y: '32%',  size: 42, delay: '0.3s', dur: '3.2s' },
        ].map((c, i) => (
          <div key={i} className="onb-culture-node" style={{
            left: c.x, top: c.y,
            width: c.size, height: c.size,
            '--culture-color': c.color,
            animationDelay: c.delay,
            animationDuration: c.dur,
          }}>
            <div className="onb-culture-core" style={{ background: c.color }}/>
            <div className="onb-culture-aura"/>
            <span className="onb-culture-lbl">{c.label}</span>
          </div>
        ))}
        {/* Connecting lines between nodes */}
        <svg className="onb-culture-lines" viewBox="0 0 420 220" preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#F5C842" stopOpacity="0"/>
              <stop offset="50%" stopColor="#F5C842" stopOpacity="0.2"/>
              <stop offset="100%" stopColor="#F5C842" stopOpacity="0"/>
            </linearGradient>
          </defs>
          <path d="M84,92 Q210,50 302,62" stroke="url(#lineGrad)" strokeWidth="0.6" fill="none" className="onb-conn-line"/>
          <path d="M84,92 Q180,140 201,136" stroke="url(#lineGrad)" strokeWidth="0.6" fill="none" className="onb-conn-line" style={{ animationDelay: '0.4s' }}/>
          <path d="M201,136 Q260,100 302,62" stroke="url(#lineGrad)" strokeWidth="0.6" fill="none" className="onb-conn-line" style={{ animationDelay: '0.8s' }}/>
          <path d="M151,158 Q180,140 201,136" stroke="url(#lineGrad)" strokeWidth="0.6" fill="none" className="onb-conn-line" style={{ animationDelay: '1.2s' }}/>
          <path d="M202,70 Q220,100 201,136" stroke="url(#lineGrad)" strokeWidth="0.6" fill="none" className="onb-conn-line" style={{ animationDelay: '1.6s' }}/>
        </svg>
      </div>

      <p className="onb-p">
        六种颜色，六个文化圈。每一盏灯背后<br/>
        都有一群守夜人，把故事写下、念出，等你路过。
      </p>

      {/* Culture legend strip */}
      <div className="onb-legend-strip">
        {[
          { color: '#D44832', label: '东亚' },
          { color: '#5B8FD6', label: '欧洲' },
          { color: '#D4A843', label: '中东' },
          { color: '#3DAD6B', label: '南亚' },
          { color: '#B05A32', label: '美洲' },
          { color: '#E07840', label: '非洲' },
        ].map((c, i) => (
          <div key={i} className="onb-legend-item">
            <span className="onb-legend-dot" style={{ background: c.color }}/>
            <span className="onb-legend-label">{c.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Step 2: The Stillness ─────────────────────────────────────────────────────
function OnbStep2() {
  return (
    <div className="onb-step onb-step-anim">
      <div className="onb-eyebrow">第 二 幕 · 静 谧</div>
      <h1 className="onb-h">
        <span className="onb-h-line">闭灯前</span>
        <span className="onb-h-line onb-h-italic">最后</span>
        <span className="onb-h-line onb-h-gold">十四分钟</span>
      </h1>
      <p className="onb-p">
        屏幕会慢慢变暗，故事会轻轻念到结尾。<br/>
        设个睡眠定时器，让世界自己合上。
      </p>

      {/* Sleep features card stack */}
      <div className="onb-sleep-cards">
        <div className="onb-sleep-card onb-sleep-card-bg"/>
        <div className="onb-sleep-card onb-sleep-card-mid"/>
        <div className="onb-sleep-card onb-sleep-card-fg">
          <div className="onb-sleep-card-header">
            <span className="onb-sleep-title">助眠播放器</span>
            <span className="onb-sleep-badge">LIVE</span>
          </div>
          <div className="onb-sleep-now">
            <div className="onb-sleep-now-dot"/>
            <span>正在播放 · 台北·中正纪念堂</span>
          </div>
          <div className="onb-sleep-bar">
            <div className="onb-sleep-bar-fill" style={{ width: '38%' }}/>
            <div className="onb-sleep-bar-head" style={{ left: '38%' }}/>
          </div>
          <div className="onb-sleep-meta">
            <span>05:21</span>
            <span>14:32</span>
          </div>
          <div className="onb-sleep-settings">
            <div className="onb-sleep-row">
              <span className="onb-sleep-icon">🌙</span>
              <span>夜间模式</span>
              <span className="onb-perm-tog onb-perm-on"><span className="onb-perm-knob"/></span>
            </div>
            <div className="onb-sleep-row">
              <span className="onb-sleep-icon">⏱</span>
              <span>睡眠定时 · 30 分钟</span>
              <span className="onb-perm-tog onb-perm-on"><span className="onb-perm-knob"/></span>
            </div>
            <div className="onb-sleep-row">
              <span className="onb-sleep-icon">🔔</span>
              <span>每晚提醒</span>
              <span className="onb-perm-tog"><span className="onb-perm-knob"/></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Step 3: The Enter ─────────────────────────────────────────────────────────
function OnbStep3({ onOpenTutorial }) {
  return (
    <div className="onb-step onb-step-anim">
      <div className="onb-eyebrow">准 备 好 了</div>
      <h1 className="onb-h">
        <span className="onb-h-line">一颗地球</span>
        <span className="onb-h-line onb-h-italic">等着</span>
        <span className="onb-h-line onb-h-gold">被你转动</span>
      </h1>
      <p className="onb-p">
        拨动地球仪，点亮一个坐标，听故事入睡。<br/>
        一切都是为了让你今晚睡个好觉。
      </p>

      {/* Key gestures illustration */}
      <div className="onb-gestures">
        <div className="onb-gesture-item">
          <div className="onb-gesture-ico">
            <svg viewBox="0 0 48 48" width="32" height="32" fill="none">
              <circle cx="24" cy="24" r="18" stroke="rgba(245,200,66,0.3)" strokeWidth="1"/>
              <path d="M14 24 Q24 10 34 24 Q24 38 14 24Z" fill="rgba(245,200,66,0.08)" stroke="rgba(245,200,66,0.4)" strokeWidth="0.8"/>
              <circle cx="24" cy="24" r="3" fill="#F5C842"/>
              <path d="M30 18 Q36 16 38 22" stroke="rgba(245,200,66,0.6)" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
              <path d="M35 20 L38 22 L36 25" stroke="rgba(245,200,66,0.6)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
          </div>
          <span className="onb-gesture-label">拖动旋转</span>
        </div>
        <div className="onb-gesture-item">
          <div className="onb-gesture-ico">
            <svg viewBox="0 0 48 48" width="32" height="32" fill="none">
              <circle cx="24" cy="24" r="18" stroke="rgba(245,200,66,0.3)" strokeWidth="1"/>
              <circle cx="24" cy="20" r="5" fill="none" stroke="rgba(245,200,66,0.6)" strokeWidth="1.2" className="onb-tap-ring"/>
              <circle cx="24" cy="20" r="2.5" fill="#F5C842"/>
              <path d="M24 28 L24 36" stroke="rgba(245,200,66,0.3)" strokeWidth="1" strokeDasharray="2,2"/>
            </svg>
          </div>
          <span className="onb-gesture-label">点击坐标</span>
        </div>
        <div className="onb-gesture-item">
          <div className="onb-gesture-ico">
            <svg viewBox="0 0 48 48" width="32" height="32" fill="none">
              <rect x="10" y="10" width="28" height="28" rx="6" fill="none" stroke="rgba(245,200,66,0.3)" strokeWidth="1"/>
              <circle cx="24" cy="22" r="7" fill="none" stroke="rgba(245,200,66,0.5)" strokeWidth="1.2"/>
              <path d="M21 22 L27 22 M24 19 L24 25" stroke="rgba(245,200,66,0.8)" strokeWidth="1.2" strokeLinecap="round"/>
              <path d="M14 32 Q24 28 34 32" stroke="rgba(245,200,66,0.3)" strokeWidth="1" strokeLinecap="round"/>
            </svg>
          </div>
          <span className="onb-gesture-label">播放故事</span>
        </div>
      </div>

      {/* Tutorial link */}
      <div className="onb-tut-callout">
        <div className="onb-tut-ico">
          <svg viewBox="0 0 20 20" width="16" height="16" fill="none">
            <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.2"/>
            <path d="M10 9.5v4M10 7h.01" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        </div>
        <span>初次使用？</span>
        <button className="onb-tut-link" onClick={onOpenTutorial}>
          查看完整使用指南 →
        </button>
      </div>
    </div>
  );
}

window.Onboarding = Onboarding;
